import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { config } from '../config';
import { getAuthToken } from '../utils/authToken';
import { fetchWithRetry } from '../utils/fetchWithRetry';

interface FeedbackBoxProps {
  pageContext: string;
}

export default function FeedbackBox({ pageContext }: FeedbackBoxProps) {
  const { instance } = useMsal();
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<'success' | 'error' | null>(null);

  const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
  const userEmail = account?.username || '';

  const handleSend = async () => {
    if (!message.trim()) return;

    setSending(true);
    setResult(null);

    try {
      const token = await getAuthToken(instance);
      if (!token) throw new Error('No token');

      const res = await fetchWithRetry(`${config.apiUrl}/sendFeedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': `Bearer ${token}`,
        },
        body: JSON.stringify({ message: message.trim(), pageContext }),
      });

      if (!res.ok) throw new Error('Send failed');

      setResult('success');
      setMessage('');
    } catch {
      setResult('error');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setResult(null);
    setMessage('');
  };

  return (
    <>
      <div style={{
        background: '#f0f4ff',
        borderRadius: 14,
        padding: 20,
        textAlign: 'center',
        border: '1px solid #dbe4ff',
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
          Chýba ti niečo?
        </div>
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0, marginBottom: 14, lineHeight: 1.5 }}>
          Napíš nám ak ti chýba nejaká analýza, niečomu nerozumieš, alebo máš nápad na vylepšenie.
        </p>
        <button
          onClick={() => setShowModal(true)}
          style={{
            padding: '10px 24px',
            borderRadius: 20,
            border: 'none',
            background: '#667eea',
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
        >
          Napísať
        </button>
      </div>

      {/* Modal overlay */}
      {showModal && (
        <div
          onClick={handleClose}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000,
            padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              maxWidth: 440,
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>
                Zdieľaj spätnú väzbu
              </h3>
              <button
                onClick={handleClose}
                style={{
                  background: 'none', border: 'none', fontSize: 20,
                  color: '#9ca3af', cursor: 'pointer', padding: '0 4px',
                }}
              >
                ✕
              </button>
            </div>

            <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, marginTop: 0, marginBottom: 16 }}>
              Tvoj feedback nám pomáha vylepšovať SmartBloček. Napíš nám čo ti chýba, čo by si zmenil, alebo ak niečomu nerozumieš.
            </p>

            {/* Email info */}
            <div style={{
              background: '#f9fafb', borderRadius: 10, padding: '10px 14px',
              marginBottom: 16, border: '1px solid #f0f0f0',
              fontSize: 12, color: '#6b7280', lineHeight: 1.5,
            }}>
              Tvoja emailová adresa <strong style={{ color: '#374151' }}>{userEmail}</strong> bude zdieľaná so SmartBloček tímom, aby sme ti mohli odpovedať.
            </div>

            {result === 'success' ? (
              <div style={{
                background: '#ecfdf5', borderRadius: 10, padding: 16,
                textAlign: 'center', color: '#065f46',
              }}>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Ďakujeme!</div>
                <div style={{ fontSize: 13 }}>Tvoj feedback bol odoslaný. Ozveme sa ti čo najskôr.</div>
                <button
                  onClick={handleClose}
                  style={{
                    marginTop: 12, padding: '8px 20px', borderRadius: 20,
                    border: '1px solid #d1d5db', background: '#fff',
                    color: '#374151', fontSize: 13, cursor: 'pointer',
                  }}
                >
                  Zavrieť
                </button>
              </div>
            ) : (
              <>
                {result === 'error' && (
                  <div style={{
                    background: '#fef2f2', color: '#b91c1c', borderRadius: 10,
                    padding: '10px 14px', marginBottom: 12, fontSize: 13,
                  }}>
                    Nepodarilo sa odoslať feedback. Skúste to prosím znova.
                  </div>
                )}

                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Napíš svoj feedback, otázku alebo nápad..."
                  maxLength={5000}
                  style={{
                    width: '100%',
                    minHeight: 120,
                    borderRadius: 10,
                    border: '1px solid #e5e7eb',
                    padding: 12,
                    fontSize: 14,
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                  onFocus={e => e.target.style.borderColor = '#667eea'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                />
                <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'right', marginTop: 4, marginBottom: 12 }}>
                  {message.length} / 5000
                </div>

                <button
                  onClick={handleSend}
                  disabled={sending || !message.trim()}
                  style={{
                    width: '100%',
                    padding: '12px 0',
                    borderRadius: 20,
                    border: 'none',
                    background: sending || !message.trim() ? '#c7d2fe' : '#667eea',
                    color: '#fff',
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: sending || !message.trim() ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s',
                  }}
                >
                  {sending ? 'Odosielam...' : 'Odoslať'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
